/**
 * Sentry integration for frontend observability.
 *
 * Features:
 *   - Error tracking (unhandled errors, promise rejections, manual captures)
 *   - Performance tracing (page loads, navigations, API calls)
 *   - Session Replay (on errors: 100%, sessions: 10% prod)
 *   - Structured logs (info/warn/error)
 *   - PII scrubbing on breadcrumbs
 *
 * Works alongside Grafana Faro:
 *   Sentry → alerting, issue tracking, replays, triaging
 *   Faro   → RUM, web vitals, OTel traces, dashboards
 */

import * as Sentry from "@sentry/react";
import { observabilityConfig } from "./env";

let _initialized = false;

export function initSentry(): void {
  if (_initialized || !observabilityConfig.sentry.enabled) return;

  Sentry.init({
    dsn: observabilityConfig.sentry.dsn,
    environment: observabilityConfig.environment,
    release: `openhub@${observabilityConfig.appVersion}`,

    // Performance — trace 10% of requests in prod, 100% in dev
    tracesSampleRate: observabilityConfig.sentry.tracesSampleRate,

    // Session Replay — always replay on error, sample 10% of sessions in prod
    replaysSessionSampleRate: observabilityConfig.sentry.replaysSessionSampleRate,
    replaysOnErrorSampleRate: observabilityConfig.sentry.replaysOnErrorSampleRate,

    // Logs
    _experiments: {
      enableLogs: true,
    },

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: true,
        blockAllMedia: false,
      }),
      Sentry.breadcrumbsIntegration({
        console: true,
        dom: true,
        fetch: true,
        history: true,
        xhr: true,
      }),
    ],

    // Trace propagation to Supabase edge functions
    tracePropagationTargets: [
      /db\.co/,
      /openhubr\.com/,
    ],

    // Filter noise
    ignoreErrors: [
      /chrome-extension/,
      /moz-extension/,
      "Failed to fetch",
      "Load failed",
      "NetworkError",
      "ResizeObserver loop",
      "AbortError",
      "NS_ERROR_NOT_INITIALIZED",
    ],
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
      /googletagmanager\.com/,
      /hotjar\.com/,
    ],

    // PII scrubbing
    beforeSend(event) {
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((bc) => {
          if (bc.data?.url) {
            try {
              const url = new URL(bc.data.url);
              ["token", "access_token", "apikey", "secret", "password"].forEach(
                (p) => url.searchParams.delete(p),
              );
              bc.data.url = url.toString();
            } catch { /* not a valid URL */ }
          }
          return bc;
        });
      }
      return event;
    },

    beforeSendTransaction(event) {
      // Drop transactions from browser extensions
      if (event.transaction?.startsWith("chrome-extension://")) return null;
      return event;
    },
  });

  _initialized = true;
}

export function setSentryUser(user: { id: string }): void {
  if (!_initialized) return;
  Sentry.setUser({ id: user.id });
}

export function clearSentryUser(): void {
  if (!_initialized) return;
  Sentry.setUser(null);
}

export function captureSentryError(
  error: unknown,
  context?: Record<string, string>,
): void {
  if (!_initialized) return;
  Sentry.withScope((scope) => {
    if (context) {
      // Promote known keys to tags (searchable/filterable in Sentry UI)
      const tagKeys = ["operation", "provider", "action", "function_name", "client_origin"];
      for (const [key, value] of Object.entries(context)) {
        if (tagKeys.includes(key)) {
          scope.setTag(key, value);
        } else {
          scope.setExtra(key, value);
        }
      }
    }
    scope.setLevel(error instanceof Error && error.message?.includes("500") ? "error" : "warning");
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
  });
}

/** Structured logging via Sentry Logs */
export const sentryLogger = {
  info(message: string, attrs?: Record<string, unknown>) {
    if (!_initialized) return;
    Sentry.logger.info(message, attrs);
  },
  warn(message: string, attrs?: Record<string, unknown>) {
    if (!_initialized) return;
    Sentry.logger.warn(message, attrs);
  },
  error(message: string, attrs?: Record<string, unknown>) {
    if (!_initialized) return;
    Sentry.logger.error(message, attrs);
  },
};

export { Sentry };
